import { faker } from '@faker-js/faker'
import { http, HttpResponse } from 'msw'
import {
  AddProjectMembershipsRequestSchema,
  CreateProjectRequestSchema,
  type ProjectMembership,
  type ProjectMembershipPaginatedResponse,
  type ProjectPaginatedResponse,
  type ProjectResponse,
  PutProjectMembershipRequestSchema,
  type Repository,
  UpdateProjectRequestSchema,
} from '../src/utils/api-client/api-schemas.ts'
import { withAuth } from './handlers.ts'

// A stateful in-memory model of the Custom Projects API, shared by dev:mocks, Storybook, vitest and
// Playwright. Membership lives on the store rather than on the repository fixtures so that a move or
// a removal is visible on the repositories list as well as on the project. Membership is a first-class
// HAL resource: a repository points at `/repositories/{id}/project-membership`, and the project it
// belongs to is reached by embedding it.

type StoredProject = { id: string; name: string; description?: string; created_at: string; feedbackCount: number }
type StoredMembership = { projectId: string; created_at: string }

export type ProjectSeed = {
  id: string
  name: string
  description?: string
  /** Positions in the seeded repository list that start out in this project. */
  repositoryIndexes?: number[]
  created_at?: string
  /** Project-scoped feedback count surfaced by the single-project GET (the mock keeps no real feedback). */
  feedbackCount?: number
}

const projects = new Map<string, StoredProject>()
/** Keyed by repository id; a repository belongs to at most one project. */
const memberships = new Map<string, StoredMembership>()
let repositories: Repository[] = []

const projectLinks = (id: string): ProjectResponse['_links'] => ({
  self: { href: `/api/v1/projects/${id}` },
  memberships: { href: `/api/v1/projects/${id}/memberships` },
  repositories: { href: `/api/v1/repositories?project=${id}` },
})

const memberRepositories = (projectId: string): Repository[] =>
  repositories
    .filter(repository => memberships.get(repository.id)?.projectId === projectId)
    .sort((a, b) => a.full_name.localeCompare(b.full_name))

const memberCount = (projectId: string) =>
  [...memberships.values()].filter(membership => membership.projectId === projectId).length

const nowIso = () => new Date().toISOString()

const pageOf = <T>(items: T[]) => ({
  _links: { self: { href: '' }, first: { href: '' } },
  page: { number: 0, size: items.length },
  total: items.length,
  _embedded: { items },
})

const toResponse = (
  project: StoredProject,
  { embedRepositories = false, single = false }: { embedRepositories?: boolean; single?: boolean } = {}
): ProjectResponse => ({
  _links: projectLinks(project.id),
  created_at: project.created_at,
  description: project.description,
  id: project.id,
  name: project.name,
  repository_count: memberCount(project.id),
  // Only the single-project representation carries the feedback count; the mock stores a seeded number.
  ...(single ? { feedback_count: project.feedbackCount } : {}),
  ...(embedRepositories
    ? { _embedded: { repositories: pageOf(memberRepositories(project.id).map(stampMembership)) } }
    : {}),
})

/** The membership resource for a repository, or `undefined` when it is ungrouped. */
const membershipFor = (repositoryId: string, { embedProject = false } = {}): ProjectMembership | undefined => {
  const membership = memberships.get(repositoryId)
  const project = membership ? projects.get(membership.projectId) : undefined
  if (!membership || !project) {
    return undefined
  }
  return {
    _links: {
      self: { href: `/api/v1/repositories/${repositoryId}/project-membership` },
      project: { href: `/api/v1/projects/${project.id}` },
      repository: { href: `/api/v1/repositories/${repositoryId}` },
    },
    created_at: membership.created_at,
    ...(embedProject ? { _embedded: { project: toResponse(project) } } : {}),
  }
}

/** Stamps a repository with its membership link (always) and the embedded membership+project (when grouped). */
const stampMembership = (repository: Repository): Repository => {
  const membership = membershipFor(repository.id, { embedProject: true })
  return {
    ...repository,
    _links: {
      ...repository._links,
      'project-membership': repository._links['project-membership'] ?? {
        href: `/api/v1/repositories/${repository.id}/project-membership`,
      },
    },
    _embedded: membership ? { ...repository._embedded, 'project-membership': membership } : repository._embedded,
  }
}

const paginate = <T>(items: T[], request: Request) => {
  const url = new URL(request.url)
  const number = Number(url.searchParams.get('page-number')) || 0
  const size = Number(url.searchParams.get('page-size')) || 10
  return {
    _links: { self: { href: url.pathname }, first: { href: url.pathname } },
    page: { number, size },
    total: items.length,
    _embedded: { items: items.slice(number * size, (number + 1) * size) },
  }
}

const notFound = (what: string) => HttpResponse.text(`${what} not found`, { status: 404 })

/** The store's view of the repositories the other mocks serve, with membership stamped on. */
export const projectStore = {
  /** Every repository the store knows about, each carrying its membership link and embedded membership. */
  repositories: (): Repository[] => repositories.map(stampMembership),
  membershipFor,
  stampMembership,
  /** Narrows repositories to the `project` query values the list endpoint accepts: ids, or `ungrouped`. */
  filterByProject: <T extends Pick<Repository, 'id'>>(items: T[], projectParams: string[]): T[] => {
    if (projectParams.length === 0) {
      return items
    }
    return items.filter(item => {
      const membership = memberships.get(item.id)
      return membership
        ? projectParams.includes(membership.projectId)
        : projectParams.some(value => value.toLowerCase() === 'ungrouped')
    })
  },
  projects: (embedRepositories = false): ProjectResponse[] =>
    [...projects.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(project => toResponse(project, { embedRepositories })),
  reset: () => {
    projects.clear()
    memberships.clear()
    repositories = []
  },
}

/**
 * Loads the store. Called once by the default handlers with the same repositories the repositories
 * mock serves; tests call it again with their own fixtures.
 */
export const seedProjectStore = ({
  repositories: seededRepositories,
  projects: seeds,
}: {
  repositories: Repository[]
  projects: ProjectSeed[]
}) => {
  projectStore.reset()
  repositories = seededRepositories
  for (const seed of seeds) {
    projects.set(seed.id, {
      id: seed.id,
      name: seed.name,
      description: seed.description,
      created_at: seed.created_at ?? '2026-09-12T10:22:00Z',
      feedbackCount: seed.feedbackCount ?? 0,
    })
    for (const index of seed.repositoryIndexes ?? []) {
      const repository = seededRepositories[index]
      if (repository) {
        memberships.set(repository.id, { projectId: seed.id, created_at: seed.created_at ?? '2026-09-12T10:22:00Z' })
      }
    }
  }
}

/** The projects of the design prototype, spread over the first repositories of the seeded list. */
export const PROTOTYPE_PROJECT_SEEDS: ProjectSeed[] = [
  {
    id: 'project-fmqpr',
    name: 'FMQPR Payments',
    description: 'Payments platform, ring-fenced',
    repositoryIndexes: [0, 1, 2, 3],
    created_at: '2026-09-12T10:22:00Z',
  },
  {
    id: 'project-retail',
    name: 'Retail Channels',
    description: 'Mobile and web front ends',
    repositoryIndexes: [4, 5, 6],
    created_at: '2026-08-28T16:15:00Z',
  },
  {
    id: 'project-treasury',
    name: 'Treasury Core',
    description: 'Ledger and settlement',
    repositoryIndexes: [7, 8],
  },
  {
    id: 'project-reporting',
    name: 'Regulatory Reporting',
    description: 'Stood up, not yet populated',
  },
  {
    id: 'project-shared',
    name: 'Shared Services',
    description: 'Platform libraries and internal tooling',
    repositoryIndexes: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22],
    created_at: '2026-09-02T09:40:00Z',
  },
]

const embedsRepositories = (request: Request) =>
  new URL(request.url).searchParams.getAll('embed').includes('items.repositories')

export const getProjects = () =>
  http.get(
    '/api/v1/projects',
    withAuth(({ request }) =>
      HttpResponse.json(
        paginate(projectStore.projects(embedsRepositories(request)), request) satisfies ProjectPaginatedResponse
      )
    )
  )

export const getProject = () =>
  http.get(
    '/api/v1/projects/:projectId',
    withAuth(({ params, request }) => {
      const project = projects.get(String(params.projectId))
      const embedRepositories = new URL(request.url).searchParams.getAll('embed').includes('items.repositories')
      return project ? HttpResponse.json(toResponse(project, { embedRepositories, single: true })) : notFound('Project')
    })
  )

const duplicateProblem = (existing: StoredProject) =>
  HttpResponse.json(
    {
      type: 'pixee:problems/api/projects/duplicate-name',
      title: 'Duplicate project',
      detail: `A project named ${existing.name} already exists.`,
      status: 409,
      _links: { project: { href: `/api/v1/projects/${existing.id}` } },
    },
    { status: 409, headers: { 'Content-Type': 'application/problem+json' } }
  )

export const postProject = () =>
  http.post(
    '/api/v1/projects',
    withAuth(async ({ request }) => {
      const body = CreateProjectRequestSchema.parse(await request.json())
      const name = body.name.trim()
      const duplicate = [...projects.values()].find(project => project.name.toLowerCase() === name.toLowerCase())
      if (duplicate) {
        return duplicateProblem(duplicate)
      }
      const project: StoredProject = {
        id: faker.string.uuid(),
        name,
        description: body.description,
        created_at: nowIso(),
        feedbackCount: 0,
      }
      projects.set(project.id, project)
      return HttpResponse.json(toResponse(project, { single: true }), {
        status: 201,
        headers: { Location: `/api/v1/projects/${project.id}` },
      })
    })
  )

export const putProject = () =>
  http.put(
    '/api/v1/projects/:projectId',
    withAuth(async ({ params, request }) => {
      const project = projects.get(String(params.projectId))
      if (!project) {
        return notFound('Project')
      }
      const body = UpdateProjectRequestSchema.parse(await request.json())
      if (body.name !== undefined) {
        const name = body.name.trim()
        const duplicate = [...projects.values()].find(
          other => other.id !== project.id && other.name.toLowerCase() === name.toLowerCase()
        )
        if (duplicate) {
          return duplicateProblem(duplicate)
        }
        project.name = name
      }
      if (body.description !== undefined) {
        project.description = body.description ?? undefined
      }
      return HttpResponse.json(toResponse(project, { single: true }))
    })
  )

export const deleteProject = () =>
  http.delete(
    '/api/v1/projects/:projectId',
    withAuth(({ params }) => {
      const projectId = String(params.projectId)
      if (!projects.delete(projectId)) {
        return notFound('Project')
      }
      for (const [repositoryId, membership] of memberships) {
        if (membership.projectId === projectId) {
          memberships.delete(repositoryId)
        }
      }
      return new HttpResponse(null, { status: 204 })
    })
  )

export const getProjectMemberships = () =>
  http.get(
    '/api/v1/projects/:projectId/memberships',
    withAuth(({ params, request }) => {
      const projectId = String(params.projectId)
      if (!projects.has(projectId)) {
        return notFound('Project')
      }
      const items = memberRepositories(projectId)
        .map(repository => membershipFor(repository.id, { embedProject: true }))
        .filter((membership): membership is ProjectMembership => membership !== undefined)
      return HttpResponse.json(paginate(items, request) satisfies ProjectMembershipPaginatedResponse)
    })
  )

export const postProjectMemberships = () =>
  http.post(
    '/api/v1/projects/:projectId/memberships',
    withAuth(async ({ params, request }) => {
      const projectId = String(params.projectId)
      if (!projects.has(projectId)) {
        return notFound('Project')
      }
      const body = AddProjectMembershipsRequestSchema.parse(await request.json())
      const items: ProjectMembership[] = []
      for (const repositoryId of new Set(body.repository_ids)) {
        const repository = repositories.find(candidate => candidate.id === repositoryId)
        if (!repository) {
          return HttpResponse.json(
            {
              type: 'pixee:problems/api/projects/unknown-repositories',
              title: 'Unknown repositories',
              detail: 'Some requested repositories do not exist. No membership was changed.',
              status: 422,
              repository_ids: [repositoryId],
            },
            { status: 422, headers: { 'Content-Type': 'application/problem+json' } }
          )
        }
        memberships.set(repositoryId, { projectId, created_at: nowIso() })
        const membership = membershipFor(repositoryId, { embedProject: true })
        if (membership) {
          items.push(membership)
        }
      }
      return HttpResponse.json({
        _links: {
          self: { href: `/api/v1/projects/${projectId}/memberships` },
          first: { href: `/api/v1/projects/${projectId}/memberships` },
        },
        page: { number: 0, size: items.length },
        total: items.length,
        _embedded: { items },
      } satisfies ProjectMembershipPaginatedResponse)
    })
  )

export const getRepositoryProjectMembership = () =>
  http.get(
    '/api/v1/repositories/:repositoryId/project-membership',
    withAuth(({ params }) => {
      const membership = membershipFor(String(params.repositoryId), { embedProject: true })
      return membership ? HttpResponse.json(membership) : notFound('Membership')
    })
  )

export const putRepositoryProjectMembership = () =>
  http.put(
    '/api/v1/repositories/:repositoryId/project-membership',
    withAuth(async ({ params, request }) => {
      const repositoryId = String(params.repositoryId)
      const body = PutProjectMembershipRequestSchema.parse(await request.json())
      if (!projects.has(body.project_id)) {
        return HttpResponse.json(
          {
            type: 'pixee:problems/api/projects/unknown-project',
            title: 'Unknown project',
            detail: 'The request names a project that does not exist.',
            status: 422,
            project_id: body.project_id,
          },
          { status: 422, headers: { 'Content-Type': 'application/problem+json' } }
        )
      }
      const wasGrouped = memberships.has(repositoryId)
      memberships.set(repositoryId, { projectId: body.project_id, created_at: nowIso() })
      const membership = membershipFor(repositoryId, { embedProject: true })
      return HttpResponse.json(membership, { status: wasGrouped ? 200 : 201 })
    })
  )

export const deleteRepositoryProjectMembership = () =>
  http.delete(
    '/api/v1/repositories/:repositoryId/project-membership',
    withAuth(({ params }) => {
      memberships.delete(String(params.repositoryId))
      return new HttpResponse(null, { status: 204 })
    })
  )

/** Every project handler, keyed the way `defaultHandlers` keys its entries. */
export const projectHandlers = () => ({
  getProjects: getProjects(),
  getProject: getProject(),
  postProject: postProject(),
  putProject: putProject(),
  deleteProject: deleteProject(),
  getProjectMemberships: getProjectMemberships(),
  postProjectMemberships: postProjectMemberships(),
  getRepositoryProjectMembership: getRepositoryProjectMembership(),
  putRepositoryProjectMembership: putRepositoryProjectMembership(),
  deleteRepositoryProjectMembership: deleteRepositoryProjectMembership(),
})
